// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import useExecutedClawbacks from '@hooks/queries/registry/clawback/useExecutedClawbacks';
import { useInstrumentConfigurationsById } from '@hooks/queries/useGetAllInstrumentConfigurations';
import React, { useMemo } from 'react';
import Loading from '../../../components/Loading';
import { DataTable } from '../../../components/Table/DataTable';
import { PageWrapper } from '../../PageWrapper';
import { ClawbackDialog } from './ClawbackDialog';
import { createHeaders } from './createHeaders';

type ClawbacksProps = {
  disableVirtualization?: boolean;
};

export const Clawbacks = ({ disableVirtualization }: ClawbacksProps) => {
  const executedClawbacks = useExecutedClawbacks();
  const instrumentsById = useInstrumentConfigurationsById();
  const [dialogName, setDialogName] = React.useState('');
  const onCloseDialog = () => {
    setDialogName('');
  };

  const isLoading = executedClawbacks.isLoading;

  const rows = useMemo(() => {
    return (executedClawbacks.data ?? []).map((r) => {
      return {
        contractId: r.contractId,
        state: 'Executed',
        payload: r.payload,
        createdAt: r.createdAt,
      };
    });
  }, [executedClawbacks.data]);

  const columns = useMemo(() => createHeaders(instrumentsById), [instrumentsById]);
  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'clawbacks',
  });
  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
    {
      name: 'clawback',
      onClick: () => setDialogName('clawback'),
    },
  ];

  if (isLoading) return <Loading />;

  return (
    <PageWrapper buttons={buttons} pageName="Clawbacks">
      {dialogName === 'clawback' && <ClawbackDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(a) => a.contractId}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
